// ABOUTME: The per-row action strip shared by the desktop rail panel and the mobile panel tiles.
// ABOUTME: Bag rows get heart · − · + · ✕; favourites rows get the filled heart alone.

import React from 'react';
import { Minus, Plus, X } from 'lucide-react';
import { GLYPHS } from './railGeometry';
import { MAX_LINE_QUANTITY } from '../../utils/cartMath';

// Figma authors exactly ONE control on a product row — the outline heart at node 45:2331 — on BOTH
// the bag frame (45:2318) and the favourites frame (45:2395), and no quantity affordance anywhere.
// Two deliberate departures, agreed with the client on 2026-08-26:
//
//   1. The heart is restored to bag rows (a previous pass replaced it with a ✕, which removed the
//      only way to save a piece out of the bag by hand), and the ✕ moves to the end of the strip.
//   2. − / + are new. The row already rendered a ×N pill for agent-driven quantities; there was no
//      manual path to that count.
//
// The heart also gains a filled state, which the design file has no artwork for: every favourites
// tile in 45:2395 uses the outline heart whether the piece is saved or not, so a guest could not
// tell a saved piece from an unsaved one. `GLYPHS.savedFilled` is the authored outline's own outer
// contour filled in, so the two states are the same silhouette.
//
// Desktop and mobile pass different unit functions (`u` vs `mu`) and different authored sizes, which
// is the whole reason this lives in one module rather than twice: the two panels' strips cannot
// drift in behaviour or in accessible naming, only in scale.

/**
 * @param {object}   props
 * @param {object}   props.item          the cart/wishlist line this strip acts on
 * @param {number}   props.quantity      the line's current quantity (>= 1)
 * @param {boolean}  props.saved         whether `item` is currently in the wishlist
 * @param {boolean}  props.showQuantity  render − / + (bag rows only)
 * @param {boolean}  props.showRemove    render ✕ (bag rows only)
 * @param {function} props.unit          authored px -> CSS length (`u` on desktop, `mu` on mobile)
 * @param {number}   props.heartW        authored heart width
 * @param {number}   props.heartH        authored heart height
 * @param {number}   props.iconSize      authored box for the stroke glyphs (− + ✕)
 * @param {number}   props.gap           authored gap between controls
 * @param {string}   props.buttonClass   extra classes per control (mobile adds `reel-touch-target`)
 * @param {object}   props.style         positioning for the strip itself (the caller owns its margin)
 */
function RowActions({
  item, quantity = 1, saved = false, showQuantity = false, showRemove = false,
  unit, heartW, heartH, iconSize, gap,
  onToggleSaved, onIncrement, onDecrement, onRemove,
  buttonClass = 'reel-touch-target relative', style,
}) {
  const title = item?.title || 'item';
  const control = `hover:opacity-70 active:opacity-70 transition-opacity text-reel-chrome-textPrimary disabled:opacity-30 disabled:pointer-events-none ${buttonClass}`;
  const box = { width: unit(iconSize), height: unit(iconSize) };
  // Touch hit box (index.css `.reel-touch-target::after`, coarse pointers only): 44px tall, but
  // exactly one PITCH wide — the control plus the gap — so four boxes tile edge to edge instead of
  // four 44px boxes overlapping on a 23px pitch, where the later sibling painted on top and a tap on
  // the right half of "+" removed the line (2026-09-03). The default class now applies on the desktop
  // composition too: an iPad gets that composition and these were 13px targets there.
  const hit = { '--hit-w': `calc(100% + ${unit(gap)})` };

  return (
    <div className="flex items-center" style={{ gap: unit(gap), ...style }}>
      {/* `aria-pressed` carries the saved state, so the accessible name can stay a stable "Save X to
          favourites" rather than flipping between two strings the way the old remove-only heart did.
          The artwork swap is the sighted equivalent of the same bit. */}
      <button
        type="button"
        aria-label={`Save ${title} to favourites`}
        aria-pressed={saved}
        onClick={() => onToggleSaved?.(item)}
        className={`block ${control}`}
        style={hit}
      >
        <img
          src={saved ? GLYPHS.savedFilled.src : GLYPHS.saved.src}
          alt=""
          aria-hidden="true"
          style={{ width: unit(heartW), height: unit(heartH) }}
        />
      </button>

      {showQuantity && (
        <>
          {/* Disabled at 1 rather than falling through to a removal: ✕ is the remove control, and a
              − that sometimes deletes the row is the class of surprise that makes guests stop using
              it. Capped at MAX_LINE_QUANTITY because the server clamps there regardless. */}
          <button
            type="button"
            aria-label={`Decrease quantity of ${title}`}
            disabled={quantity <= 1}
            onClick={() => onDecrement?.(item)}
            className={`block ${control}`}
            style={hit}
          >
            <Minus aria-hidden="true" style={box} />
          </button>
          <button
            type="button"
            aria-label={`Increase quantity of ${title}`}
            disabled={quantity >= MAX_LINE_QUANTITY}
            onClick={() => onIncrement?.(item)}
            className={`block ${control}`}
            style={hit}
          >
            <Plus aria-hidden="true" style={box} />
          </button>
        </>
      )}

      {showRemove && (
        <button
          type="button"
          aria-label={`Remove ${title}${quantity > 1 ? ` (×${quantity})` : ''} from bag`}
          onClick={() => onRemove?.(item)}
          className={`block ${control}`}
          style={hit}
        >
          <X aria-hidden="true" style={box} />
        </button>
      )}
    </div>
  );
}

export default RowActions;
